/**
 * The two steps between pressing Answer and the audio starting, shared by the
 * incoming card and the call-waiting knock: claim the row, then route the audio.
 *
 * Moved out of `CallProvider.acceptCall` unchanged when the knock became a
 * second way to answer. Two copies of "a 409 on a parked call is a decision"
 * would be how the two paths start disagreeing about who gets the caller.
 */
import { ApiError } from "../../../lib/api";
import { claimCall, type CallDetail, type ParkedOffer } from "../../../lib/calls";
import { applyAudioInput, applyAudioOutputs, type CallAudio } from "../audio/apply";
import { readAudioChoice } from "../audio/choice";
import { applyMicGain } from "../audio/gain";
import { readAudioVolume } from "../audio/volume";

export type ClaimOutcome =
  /** A colleague has this caller, or the caller is gone. Do not answer. */
  | { refused: true }
  /**
   * Answer. `callLogId` is the row the claim was made on ("" when there was
   * none to claim), and `recording` is the row's own flag, or null when the
   * claim did not come back with a row.
   */
  | { refused: false; callLogId: string; recording: boolean | null };

export interface ClaimOfferInput {
  /** The row id the offer carried, "" on a parked offer. */
  callLogId: string;
  /** The offer's own leg sid on a parked offer, "" on the `<Dial>` path. */
  legSid: string;
  /** The row lookup already running for a parked offer, if any. */
  parked: Promise<ParkedOffer | null> | null;
  /** Injected in tests. */
  claim?: (id: string, legSid?: string) => Promise<CallDetail>;
}

/**
 * Claim the offer's row BEFORE the audio starts, not after.
 *
 * The claim is what stamps this operator onto the row, and the co-pilot's
 * first window is refused until it has - so a claim racing the tap would lose
 * the opening of the call.
 */
export async function claimOffer(input: ClaimOfferInput): Promise<ClaimOutcome> {
  const claim = input.claim ?? claimCall;
  // A parked offer's row may still be on its way. The card was allowed up
  // without it; the claim is not, because on that bridge the claim is what
  // decides which operator gets the caller.
  const id =
    input.callLogId ||
    (input.legSid && input.parked ? ((await input.parked)?.callLogId ?? "") : "");
  if (!id) return { refused: false, callLogId: "", recording: null };

  const claimed = await claim(id, input.legSid || undefined).then(
    (row) => row,
    (err: unknown) =>
      input.legSid && err instanceof ApiError && err.status === 409 ? ("refused" as const) : null,
  );
  // ON A PARKED CALL A 409 IS A DECISION, not a failure: a colleague has this
  // caller, or the caller is gone. Answering anyway would walk into the room
  // and be dropped a second later with the caller mid-sentence, so the offer
  // is turned down instead - what Twilio's own fan-out used to do to the
  // slower browser. Every other failed claim is bookkeeping, and must not cost
  // the conversation.
  if (claimed === "refused") return { refused: true };
  // The recording dot comes from the row rather than from the account setting,
  // exactly as it does outbound: what the row says is what the TwiML was built
  // with.
  return { refused: false, callLogId: id, recording: claimed ? claimed.recordEnabled === true : null };
}

/**
 * The same routing the outbound path applies, at the same moment relative to
 * the audio starting. An answered call that came out of the wrong speaker is
 * the identical complaint, and the operator has even less time to notice it:
 * somebody is already talking.
 */
export async function routeAnswerAudio(audio: () => CallAudio | null): Promise<void> {
  const chosen = readAudioChoice();
  await applyAudioOutputs(audio(), chosen);
  await applyMicGain(audio(), readAudioVolume().input);
  await applyAudioInput(audio(), chosen);
}
